/**
 * تتبّع أحداث الكاروسيلات (GA4) لقياس ثبات السحب بعد الإطلاق.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * الأحداث المُرسَلة:
 *  - carousel_swipe           → سحب (نجاح أو إلغاء — يُميَّز بـswipe_outcome)
 *  - carousel_arrow_click     → نقرة سهم (ديسكتوب)
 *  - carousel_inner_collision → السحب رُفض من الكاروسيل الخارجيّ
 *
 * البارامترات الموحَّدة في كلّ حدث:
 *  - event_category    : "carousel" (ثابت)
 *  - carousel_name     : snake_case نظيف (lowercase, بدون مسافات/رموز)
 *  - swipe_outcome     : "success" | "aborted" | "collision"
 *  - nav_direction     : "next" | "prev"        (اتّجاه الانتقال المنطقيّ)
 *  - text_direction    : "rtl" | "ltr"           (اتّجاه الواجهة وقت الحدث)
 *  - page_context      : pathname للصفحة
 *  - from_index/to_index/total_slides
 *  - swipe_duration_ms : الزمن بين pointerDown و pointerUp
 *  - count             : عند تجميع أحداث متلاحقة (rate-limit)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Custom Dimensions الموصى بتسجيلها يدويّاً في GA4 Admin (Event scope):
 *   1. carousel_name     ← اسم الكاروسيل (للمقارنة بين كاروسيلات الموقع)
 *   2. swipe_outcome     ← نتيجة السحب (success/aborted/collision)
 *   3. nav_direction     ← اتّجاه الانتقال (next/prev)
 *   4. text_direction    ← اتّجاه الواجهة (rtl/ltr) — لكشف مشاكل اللغة العربيّة
 *   5. page_context      ← مسار الصفحة — لتمييز نفس الكاروسيل في صفحات متعدّدة
 *
 * المسار في GA4: Admin → Custom definitions → Create custom dimension
 *   - Dimension name: <كما أعلاه>
 *   - Scope: Event
 *   - Event parameter: <نفس الاسم بالضبط>
 *
 * بعد التسجيل تظهر هذه الأبعاد في Explorations ويمكن الفلترة/المقارنة بها.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * SSR-safe: كلّ شيء وراء `typeof window`.
 */
import type { EmblaCarouselType } from "embla-carousel";

type GtagFn = (...args: unknown[]) => void;
type EventParams = Record<string, unknown>;

function pushDataLayer(eventName: string, params: EventParams) {
  if (typeof window === "undefined") return;
  const w = window as unknown as { dataLayer?: unknown[]; gtag?: GtagFn };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(["event", eventName, params]);
}

/** ينظّف اسم الكاروسيل: lowercase + استبدال غير الأبجدرقميّ بـ`_`. */
function cleanName(name: string): string {
  return (name || "unknown")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

function pagePath(): string {
  if (typeof window === "undefined") return "";
  try {
    return window.location.pathname || "";
  } catch {
    return "";
  }
}

function textDirection(): "rtl" | "ltr" {
  if (typeof document === "undefined") return "ltr";
  return document.documentElement.dir === "rtl" ? "rtl" : "ltr";
}

/** بارامترات أساسيّة لكلّ حدث كاروسيل. */
function baseParams(name: string, extra: EventParams = {}): EventParams {
  return {
    event_category: "carousel",
    carousel_name: cleanName(name),
    text_direction: textDirection(),
    page_context: pagePath(),
    ...extra,
  };
}

/* -------------------------------------------------------------------------- */
/* Rate limit بسيط: تجميع أحداث متلاحقة على نفس (event+name) داخل نافذة 1s.   */
/* يحمي GA4 من ضخّ مفرط عند سحب عصبيّ.                                       */
/* -------------------------------------------------------------------------- */
const RATE_WINDOW_MS = 1000;
const RATE_THRESHOLD = 5;
type Bucket = { count: number; firstTs: number; lastParams: EventParams; timer: ReturnType<typeof setTimeout> | null };
const buckets = new Map<string, Bucket>();

function emit(eventName: string, params: EventParams) {
  const key = `${eventName}|${params.carousel_name}|${params.swipe_outcome ?? ""}`;
  const now = Date.now();
  const b = buckets.get(key);

  if (!b) {
    buckets.set(key, { count: 1, firstTs: now, lastParams: params, timer: null });
    pushDataLayer(eventName, params);
    return;
  }

  // ضمن النافذة
  if (now - b.firstTs < RATE_WINDOW_MS) {
    b.count += 1;
    b.lastParams = params;
    if (b.count <= RATE_THRESHOLD) {
      pushDataLayer(eventName, params);
    } else if (!b.timer) {
      // فوق العتبة → ابدأ مؤقّتاً لإرسال حدث مُجمَّع في نهاية النافذة
      b.timer = setTimeout(() => {
        const bucket = buckets.get(key);
        if (bucket && bucket.count > RATE_THRESHOLD) {
          pushDataLayer(eventName, {
            ...bucket.lastParams,
            count: bucket.count - RATE_THRESHOLD,
            rate_limited: true,
          });
        }
        buckets.delete(key);
      }, RATE_WINDOW_MS);
    }
    return;
  }

  // خارج النافذة → أعد التهيئة
  if (b.timer) clearTimeout(b.timer);
  buckets.set(key, { count: 1, firstTs: now, lastParams: params, timer: null });
  pushDataLayer(eventName, params);
}

/* -------------------------------------------------------------------------- */
/* الواجهات المُصدَّرة                                                        */
/* -------------------------------------------------------------------------- */

export function trackCarouselArrowClick(name: string, direction: "next" | "prev") {
  emit("carousel_arrow_click", baseParams(name, {
    nav_direction: direction,
    swipe_outcome: "success",
  }));
}

export function trackCarouselInnerCollision(outerName: string) {
  emit("carousel_inner_collision", baseParams(outerName, {
    swipe_outcome: "collision",
  }));
}

/**
 * يربط مستمعي Embla ويُعيد دالّة تنظيف.
 * يقيس: نجاح/إلغاء السحب + مدّته + الاتجاه.
 */
export function attachCarouselTracking(
  emblaApi: EmblaCarouselType | undefined,
  name: string,
): () => void {
  if (!emblaApi || typeof window === "undefined") return () => {};

  let indexAtPointerDown: number | null = null;
  let pointerDownTs: number = 0;
  let changedSincePointerDown = false;

  const onPointerDown = () => {
    indexAtPointerDown = emblaApi.selectedScrollSnap();
    pointerDownTs = Date.now();
    changedSincePointerDown = false;
  };

  const onSelect = () => {
    if (indexAtPointerDown !== null) changedSincePointerDown = true;
  };

  const onPointerUp = () => {
    if (indexAtPointerDown === null) return;
    const from = indexAtPointerDown;
    const to = emblaApi.selectedScrollSnap();
    const duration = Date.now() - pointerDownTs;
    indexAtPointerDown = null;

    const success = changedSincePointerDown && from !== to;
    const total = emblaApi.slideNodes().length;

    emit("carousel_swipe", baseParams(name, {
      swipe_outcome: success ? "success" : "aborted",
      nav_direction: success ? (to > from ? "next" : "prev") : "none",
      from_index: from,
      to_index: success ? to : from,
      total_slides: total,
      swipe_duration_ms: duration,
    }));
  };

  emblaApi.on("pointerDown", onPointerDown);
  emblaApi.on("pointerUp", onPointerUp);
  emblaApi.on("select", onSelect);

  return () => {
    emblaApi.off("pointerDown", onPointerDown);
    emblaApi.off("pointerUp", onPointerUp);
    emblaApi.off("select", onSelect);
  };
}
